// Tab1.js
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Skeleton,
  FormControl,
  ButtonGroup,
  IconButton,
  Tooltip,
  Autocomplete,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DateFilter from "./DateFilter.jsx";
import { useNavigate } from "react-router-dom";
import {
  retrieveDueUser,
  AutocompleteDueUser,
} from "../Apihelper/DueUserApi.js";
import AppToast from '../../components/Toast/AppToast';
import MemberAvatar from './MemberAvatar';

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    borderBottom: 'none',
    padding: '8px 10px',
    whiteSpace: 'normal',
    fontSize: 12,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '6px 10px',
    whiteSpace: 'normal',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f9f9f9',
  },
  '&:hover': {
    backgroundColor: 'rgba(31, 122, 84, 0.08)',
    cursor: 'pointer',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const Tab1 = (props) => {
  const navigate = useNavigate();
  const { colors: appColors } = useAppTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [options, setOptions] = useState([]);
  const [fetchdatarow, setetchdatarow] = useState([]);
  const [datelist, setDatelist] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [todate, settodate] = useState("");
  const dateRef = useRef();

  useEffect(() => {
    const storedData = localStorage.getItem("userdata");
    if (!storedData) {
      navigate('/');
    }
  }, []);

  const handleSnackbarOpen = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenToast(true);
  };

  const handleToastClose = () => {
    setOpenToast(false);
  };

  const fetchData = async (value = "", newPage = 0, newRowsPerPage = rowsPerPage) => {
    setLoading(true);
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const res = await retrieveDueUser(
        userToken, userId, value, newRowsPerPage, newPage * newRowsPerPage,
        datelist, fromDate, "",
      );
      if (res.code == "200") {
        setetchdatarow(res.data);
        const serverTotal = res.total_count ?? res.recordsTotal ?? res.recordsFiltered ?? res.total ?? null;
        if (serverTotal != null) {
          setTotalCount(serverTotal);
        } else if (res.data.length < newRowsPerPage) {
          setTotalCount(newPage * newRowsPerPage + res.data.length);
        } else {
          setTotalCount((prev) => Math.max(prev, (newPage + 1) * newRowsPerPage + 1));
        }
      } else {
        setetchdatarow([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchData("", newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchData("", 0, newSize);
  };

  const handleCardPress = async (member_id) => {
    try {
      navigate("/memberdetails", {
        state: {
          id: member_id,
          random_no: Math.random(),
        },
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSendEmail = async (memberId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
      const res = await fetch(`${window.constants.api_url}/due-email/due-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userData.token}` },
        body: JSON.stringify({ member_id: memberId }),
      });
      const data = await res.json();
      handleSnackbarOpen(data.message, data.code == 200 ? 'success' : 'error');
    } catch (e) {
      handleSnackbarOpen('Failed to send email', 'error');
    }
  };

  const handleSendWhatsApp = async (memberId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
      const res = await fetch(`${window.constants.api_url}/due-whatsapp/due-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userData.token}` },
        body: JSON.stringify({ member_id: memberId }),
      });
      const data = await res.json();
      handleSnackbarOpen(data.message, data.code == 200 ? 'success' : 'error');
    } catch (e) {
      handleSnackbarOpen('Failed to send WhatsApp', 'error');
    }
  };

  const handleSearchChange = (event, value) => {
    setPage(0);
    fetchData(value || "", 0, rowsPerPage);
  };

  const handleInputChange = async (value) => {
    if (value.length >= 3) {
      try {
        const userData = localStorage.getItem("userdata");
        const userDataObj = JSON.parse(userData);
        const userToken = userDataObj.token;
        const userId = userDataObj.id;
        const searchText = value;

        const res = await AutocompleteDueUser(userToken, userId, searchText);

        if (res.code == "200") {
          const dataArray = Object.values(res.data);
          if (Array.isArray(dataArray)) {
            const names = dataArray.map((item) => item.search_text);
            setOptions(names);
          } else {
            console.error("res.data is not an array:", res.data);
          }
        } else {
          handleSnackbarOpen(res.message, "error");
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleApplydatecustom = async ({ fromDateval, toDateval, value = '' }) => {
    settodate(toDateval);
    setFromDate(fromDateval);
    setLoading(true);
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const searchText = value;
      const limit = 10;
      const index = 0;

      const data = await retrieveDueUser(
        userToken,
        userId,
        searchText,
        limit,
        index,
        datelist,
        fromDateval,
        toDateval,
      );
      if (data.code == "200") {
        setetchdatarow(data.data);
        setPage(0);
        const serverTotal = data.recordsTotal ?? data.recordsFiltered ?? data.total ?? null;
        if (serverTotal != null) setTotalCount(serverTotal);
        else setTotalCount(data.data.length < rowsPerPage ? data.data.length : data.data.length + 1);
      } else {
        setetchdatarow([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplydate = async (selectedValue, value = '') => {
    setDatelist(selectedValue);
    setLoading(true);
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const data = await retrieveDueUser(
        userDataObj.token, userId, value, rowsPerPage, 0,
        selectedValue, "", "",
      );
      if (data.code == "200") {
        setetchdatarow(data.data);
        setPage(0);
        const serverTotal = data.recordsTotal ?? data.recordsFiltered ?? data.total ?? null;
        if (serverTotal != null) setTotalCount(serverTotal);
        else setTotalCount(data.data.length < rowsPerPage ? data.data.length : data.data.length + 1);
      } else {
        setetchdatarow([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Search and Filter Section */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <Autocomplete
          freeSolo
          disableClearable
          options={options}
          onInputChange={(event, value) => handleInputChange(value)}
          onChange={(event, value) => handleSearchChange(event, value)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search members"
              fullWidth
              InputProps={{
                ...params.InputProps,
                type: 'search',
              }}
              sx={{
                width: { xs: '100%', sm: '300px' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                }
              }}
            />
          )}
        />
        
        <FormControl sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <DateFilter 
            ref={dateRef}
            onApplycustomdate={handleApplydatecustom}
            onApply={handleApplydate}
          />
        </FormControl>
      </Box>

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 3,
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          overflowX: 'auto'
        }}
      >
          <Table sx={{ width: '100%', minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ width: 36 }}>#</StyledTableCell>
                <StyledTableCell>MEMBER</StyledTableCell>
                <StyledTableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>PHONE</StyledTableCell>
                <StyledTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>EMAIL</StyledTableCell>
                <StyledTableCell>PACKAGE</StyledTableCell>
                <StyledTableCell align="center">DUE ₹</StyledTableCell>
                <StyledTableCell align="center">DUE DATE</StyledTableCell>
                <StyledTableCell align="center">ACTIONS</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={20} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton variant="circular" width={34} height={34} />
                      <Box><Skeleton width={110} /><Skeleton width={80} height={10} /></Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={90} /></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={140} /></TableCell>
                  <TableCell><Skeleton width={70} /></TableCell>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                </TableRow>
              )) : fetchdatarow.map((row, index) => (
                <StyledTableRow key={row.id}>
                  <TableCell sx={{ color: '#9ca3af', fontSize: 11 }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MemberAvatar image={row.image} name={row.name} size={34} />
                      <Box>
                        <Typography
                          onClick={() => handleCardPress(row.member_id)}
                          sx={{ fontSize: 13, fontWeight: 600, color: appColors.leaf, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {row.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{row.membership_number}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, display: { xs: 'none', sm: 'table-cell' } }}>{row.phone}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#6b7280', display: { xs: 'none', md: 'table-cell' } }}>{row.email}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{row.package_name}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'inline-block', bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 12, px: 1.2, py: 0.4, borderRadius: 1 }}>
                      ₹{row.due}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 12, color: '#374151' }}>{row.end_date}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Send WhatsApp">
                        <IconButton size="small" sx={{ color: '#25d366', '&:hover': { bgcolor: 'rgba(37,211,102,0.08)' } }} onClick={() => handleSendWhatsApp(row.member_id)}>
                          <WhatsAppIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Email">
                        <IconButton size="small" sx={{ color: appColors.leaf, '&:hover': { bgcolor: 'rgba(31,122,84,0.08)' } }} onClick={() => handleSendEmail(row.member_id)}>
                          <EmailIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50]}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showFirstButton
        showLastButton
        sx={{ '& .MuiTablePagination-actions': { mr: 8 } }}
        labelDisplayedRows={({ page: p, count }) =>
          `Page ${p + 1} of ${Math.ceil(count / rowsPerPage)}  |  Total: ${count} records`
        }
      />

      <AppToast open={openToast} message={snackbarMessage} severity={snackbarSeverity} onClose={handleToastClose} />
    </Box>
  );
};

export default Tab1;
